<template>
  <div class="word-cloud-grouping" :class="{'white-text': isTextColourWhite}">
    <div
      class="word-cloud-grouping--pill animate__animated animate__fadeIn animate__faster"
      @click="toggleWordCloudGrouping"
      :class="{'is-hight-lighted': isGroupButtonHighLighted,'disabled': isDisabled}"
      v-tooltip="wordCloudTooltip"
    >
      <div
        class="toggler__wrapper"
        :class="{ 'showing-grouping-word-cloud': isShowingGroupWordCloud }"
      >
        <div class="grouping-button__content">
          <span class="loading-spinner" v-if="isLoading" :class="{'black': slideActive.textColour == '#313131'}" />
          <aha-icon
            name="word-cloud"
            width="24"
            height="24"
            viewBox="0 0 30 30"
            v-if="!isLoading && isShowingGroupWordCloud"
          ></aha-icon>
          <aha-icon
            name="word-cloud"
            width="24"
            height="24"
            viewBox="0 0 30 30"
            v-if="!isLoading && !isShowingGroupWordCloud"
          ></aha-icon>
          <p class="ml-1.5 font-bold">
            {{ wordCloudButtonText }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import utils from '@/util'
import { PRESENTING_MODE } from '@/constant/eventCategories'

const WORD_CLOUD_GROUPING_PRELOAD_THRESHOLD = 20

export default {
  name: 'WordCloudGroupingToggler',
  props: {
    isShowingGroupWordCloud: {
      type: Boolean,
      default: false,
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      WORD_CLOUD_GROUPING_PRELOAD_THRESHOLD,
    }
  },
  computed: {
    ...mapGetters(['isPresenting', 'isBackstage']),
    ...mapState({
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      isSharingView: (state) => state.sharePresentation,
      userOnline: (state) => state.userOnline,
      numberOfWordsInGroup: (state) => state.slideActive.numberOfWordsInGroup,
      numberOfUniqueWordsInGroup: (state) => state.slideActive.numberOfUniqueWordsInGroup,
      wordCloudSmartGrouping: (state) => state.slideActive.wordCloudSmartGrouping,
      slideActiveId: (state) => state.slideActive.id,
      slideActive: (state) => state.slideActive,
    }),
    wordCloudTooltip() {
      return this.isShowingGroupWordCloud ? this.$t('Ungroup the answers') : this.$t('Group the answers by meaning')
    },
    wordCloudButtonText() {
      return this.isShowingGroupWordCloud ? this.$t('Ungroup') : this.$t('Group')
    },
    isTextColourWhite() {
      return this.slideActive.textColour === '#ffffff'
    },
    isGroupButtonHighLighted() {
      return this.numberOfUniqueWordsInGroup
        && this.numberOfUniqueWordsInGroup >= this.WORD_CLOUD_GROUPING_PRELOAD_THRESHOLD
        && !this.isDisabled
        && !this.isShowingGroupWordCloud
    },
  },
  watch: {
    async slideActiveId() {
      this.isLoading = false
    },
  },
  methods: {
    toggleWordCloudGrouping() {
      if (this.isLoading || this.isDisabled) return
      const self = this
      this.isLoading = true
      this._trackToggleWordCloudGrouping()
      this.$emit('toggleWordCloudGrouping', this.slideActiveId, !this.isShowingGroupWordCloud, () => {
        self.isLoading = false
      })
    },
    _trackToggleWordCloudGrouping() {
      const eventAction = this.isShowingGroupWordCloud ? 'Grouping > Ungroup Words' : 'Grouping > Group Words'
      const trackData = {
        eventAction,
        eventCategory: PRESENTING_MODE,
        presentationId: this.presentation.id,
        userId: this.currentUser.id,
        submissionCount: this.slideActive.SlideOptions.length || 0,
        audienceCount: this.userOnline || 0,
        slideId: this.slideActive.id,
        slideType: 'wordcloud',
      }
      utils.trackGA4AndMixpanel(eventAction, trackData)
    },
  },
}
</script>

<style lang="scss" scoped>
.word-cloud-grouping {
  @apply ml-4 relative;
  &:before {
    width: 1px;
    content: "";
    left: 0;
    top: 0.4rem;
    height: 25px;
    position: absolute;
    background-color: #D3D7E1;
    transition: background-color 0.2s ease-out;
  }
  &.white-text {
    &:before {
      background-color: rgba(255, 255, 255, 0.2);
    }
    .word-cloud-grouping--pill {
      &:hover {
        background-color: rgba(255, 255, 255, 0.2);
      }
      &.is-hight-lighted:hover {
        @apply bg-primary-100;
      }
      &.disabled {
        color: rgba(255,255,255,0.5);
        @apply cursor-not-allowed;
        &:hover {
          background-color: unset;
        }
      }
    }
  }
}
.word-cloud-grouping--pill {
  @apply rounded-3xl cursor-pointer ml-2;
  background-color: rgba(255, 255 , 255, 0);
  padding: 0.4rem 0.75rem;
  &:hover {
    @apply bg-base-30;
  }
  &.disabled {
    color: rgba(0,0,0,0.5);
    @apply cursor-not-allowed;
    &:hover {
      background-color: unset;
    }
  }
  &.is-hight-lighted {
    @apply bg-primary-90 text-base-0;
    &:hover {
      @apply bg-primary-100;
    }
    .loading-spinner {
      border: 0.15em solid rgba(255, 255, 255, 0.3);
      border-top-color: #ffffff;
    }
  }
}
.toggler__wrapper {
  @apply flex text-base;
  .grouping-button__content {
    @apply inline-flex;
  }
}
.loading-spinner {
  width: 1.25em;
  height: 1.25em;
  margin: 2px;
}
</style>
